<template>
  <v-dialog v-model="showManageTeamsModal" persistent width="50vw">
    <v-card>
      <v-card-title>
        {{ getPageTitle }}
      </v-card-title>
      <v-card-text>
        <v-alert v-if="manageTeamError" type="error" class="alert-message">
          {{ manageTeamError }}
        </v-alert>
        <div v-if="page === TEAM_EDIT_PG">
          <v-text-field
            v-model="search"
            label="Type to filter the list, enter a new team name and press Enter to create, or hover and click the icon to edit."
            clearable
            @click:clear="search = ''"
            class="mb-4"
            @keydown.enter="createTeam"
          />
          <v-list style="height: 50vh; max-height: 50vh; overflow-y: auto;">
            <v-list-item
              v-for="team in filteredTeams"
              :key="team.id"
              :class="{ 'selected-team': selectedTeam.id === team.id }"
              :disabled="disabled"
              style="cursor: pointer;"
              class="team-list-item"
            >
              <v-list-item-content>
              <v-list-item-title v-if="selectedTeam.id !== team.id || !selectedTeam.editing" @click="selectedTeam = { ...team, editing: false }">
                {{ team.team_name }} ({{ team.associations ? team.associations.length : 0 }})
              </v-list-item-title>
                <div v-else style="display: flex; align-items: center;">
                <v-text-field
                  v-model="selectedTeam.team_name"
                  underlined
                  @keydown.enter="$emit('EDIT_TEAM_NAME', selectedTeam)"
                  class="pa-0 ma-0 slim-text-field"
                  style="flex: 1;"
                  hide-details
                  :disabled="disabled"
                  :maxlength="128"
                />
                <v-icon
                  class="ml-2"
                  style="cursor: pointer;"
                  @click="editTeamName(selectedTeam)"
                  title="Save"
                >
                  mdi-check
                </v-icon>
                <v-icon
                  class="ml-1"
                  style="cursor: pointer;"
                  @click="clearSelectedTeam"
                  title="Cancel"
                >
                  mdi-close
                </v-icon>
                </div>
              </v-list-item-content>
              <v-spacer />
              <v-icon
              v-if="selectedTeam.id !== team.id || !selectedTeam.editing"
              class="ml-2 edit-icon"
              style="cursor: pointer;"
              @click="selectedTeam = { ...team, editing: true }"
              >
              mdi-pencil-box-outline
              </v-icon>
            </v-list-item>
          </v-list>
        </div>
        <div v-else-if="page === TEAM_DELETE_PG">
            <div
            v-if="selectedTeam && selectedTeam.id"
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;"
            class="black--text"
            >
              <p>You're about to delete a team with {{ getDeleteAssociationText() }} assigned. This action is permanent and cannot be undone.</p>
              <p>Are you sure you want to proceed with Delete?</p>
            </div>
        </div>
        <div v-else-if="page === TEAM_ASSIGN_PG">
            <div v-if="selectedTeam && selectedTeam.id" class="black--text">
            <p>Select from the list of Users or Agents below to associate with "{{ selectedTeam.team_name }}"</p>
            <p>Note: An agent/user can be assigned to a maximum of 50 teams.</p>
            <v-list style="height: 50vh; max-height: 50vh; overflow-y: auto;" class="ma-0 pa-0">
              <v-list-item
              v-for="agent in sortedAgentData"
              :key="agent.id"
              >
                <v-checkbox
                :input-value="selectedTeam.associations && selectedTeam.associations.some(a => a.profile_id === agent.id)"
                @change="toggleAgentAssociation(agent, $event)"
                :label="agent.name"
                :disabled="disabled"
                hide-details
                class="profile-checkbox""
                />
              </v-list-item>
            </v-list>
            </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="page > TEAM_EDIT_PG"
          color="primary"
          :disabled="!selectedTeam.id"
          @click="moveToPreviousPage(page)"
        >
          Back
        </v-btn>
        <v-btn
          v-if="page === TEAM_EDIT_PG"
          color="primary"
          :disabled="!selectedTeam.id"
          @click="moveToNextPage(TEAM_ASSIGN_PG)"
        >
          Assign
        </v-btn>
        <v-btn
          v-if="page === TEAM_EDIT_PG"
          color="primary"
          :disabled="!selectedTeam.id"
          @click="moveToNextPage(TEAM_DELETE_PG)"
        >
          Delete
        </v-btn>
        <v-btn
          v-if="page === TEAM_ASSIGN_PG"
          color="primary"
          :disabled="disabled"
          @click="saveSelectedTeam()"
        >
          Save
        </v-btn>
        <v-btn
          v-else-if="page === TEAM_DELETE_PG"
          color="primary"
          @click="deleteSelectedTeam()"
          :disabled="disabled"
        >
          Delete
        </v-btn>
        <v-btn color="primary" @click="hideModal(true)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

// constants for the team management pages
const TEAM_EDIT_PG = 1    // constant for the team edit page
const TEAM_DELETE_PG = 2  // constant for the team delete page
const TEAM_ASSIGN_PG = 3  // constant for the team assign page

export default {
  name: 'ManageTeams',
  props: {
    // prop to control the visibility of the modal
    showManageTeamsModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      manageTeamError: null, // error message for managing teams
      search: '', // search input for filtering teams
      selectedTeam: { id: null, team_name: '' }, // initialize with an empty object to avoid errors
      disabled: false, // disable state for the input field
      page: TEAM_EDIT_PG, // page state to toggle between team edit and delete view
      editing: false, // state to track if a team is being edited
    }
  },
  methods: {
    ...mapActions("resources", ["retrieveTeamData", "retrieveAgentData", "saveTeam", "deleteTeam"]),
    // function to hide any existing error messages
    hideErrors() {
      this.manageTeamError = null
    },
    // function to clear the search input and selected team
    clearFields() {
      this.search = ''
      this.selectedTeam = { id: null, team_name: '' }
    },
    // function to clear the selected team and reset the editing state
    clearSelectedTeam() {
      this.selectedTeam = { id: null, team_name: '' }
      this.editing = false // reset editing state
    },
    // function to move to the previous page (assign team page)
    moveToPreviousPage(pg) {
      this.hideErrors() // hide any existing errors when moving to the previous page
      this.page = TEAM_EDIT_PG // reset to the team edit page
    },
    // function to move to the next page (delete team page)
    moveToNextPage(pg) {
      this.hideErrors() // hide any existing errors when moving to the next page
      this.page = pg // set the current page to the specified page
    },
    // function to hide the modal and reset the state
    hideModal(reload) {
      this.hideErrors()
      this.clearFields()
      // emit an event to hide the modal
      this.$emit("HIDE_MANAGE_TEAMS_MODAL", { reload: reload })
      setTimeout(() => {
        this.page = TEAM_EDIT_PG // reset to the first page after half a second
      }, 500)
    },
    // function to create a new team
    async createTeam() {
      if (this.search.trim() === '') return // prevent creating empty teams
      if (this.teamData.find(team => team.team_name.toLowerCase() === this.search.trim().toLowerCase())) return // don't create a team if it already exists
      this.disabled = true // disable the input field while creating the team
      let success = await this.saveTeam({
        team_name: this.search.trim(),
        profile_json: null
      })
      if (!success) {
        this.manageTeamError = 'Failed to create team. Please try again.'
        this.disabled = false // re-enable the input field if creation fails
        return
      }
      this.retrieveTeamData() // refresh the teams list
      this.search = '' // clear the search input after creating a team
      this.disabled = false // re-enable the input field after creation
    },
    // function to handle the editing of a team name
    async editTeamName(team) {
      if (!team || !team.id || !team.team_name.trim()) return // validate team data
      this.disabled = true // disable the input field while saving
      let success = await this.saveTeam({
        id: team.id,
        team_name: team.team_name.trim(),
        profile_json: null
      })
      if (!success) {
        this.manageTeamError = 'Failed to update team name. Please try again.'
        this.disabled = false // re-enable the input field if update fails
        return
      }
      this.retrieveTeamData() // refresh the teams list after updating
      this.clearSelectedTeam() // clear the selected team after saving
      this.disabled = false // re-enable the input field after saving
      this.editing = false // reset editing state
    },
    // function to save the selected team with its associations
    async saveSelectedTeam() {
      if (!this.selectedTeam || !this.selectedTeam.id) return // validate selected team
      this.disabled = true // disable the button while saving
      let success = await this.saveTeam({
        id: this.selectedTeam.id,
        profile_json: JSON.stringify(this.teamAssociatedProfiles)
      })
      if (!success) {
        this.manageTeamError = 'Failed to save team. Please try again.'
        this.disabled = false // re-enable the button if saving fails
        return
      }
      this.retrieveTeamData() // refresh the teams list after saving
      this.hideModal(true) // hide the modal after saving
      this.disabled = false // re-enable the button after saving
    },
    // function to get the text for the delete confirmation based on the number of associations
    getDeleteAssociationText() {
      const count = this.selectedTeam.associations ? this.selectedTeam.associations.length : 0
      const noun = count === 1 ? 'user or agent' : 'users or agents'
      return `${count} active ${noun}`
    },
    // function to handle the deletion of a selected team
    async deleteSelectedTeam() {
      if (!this.selectedTeam || !this.selectedTeam.id) return
      this.disabled = true // disable the button while deleting
      let success = await this.deleteTeam({
        id: this.selectedTeam.id
      })
      if (!success) {
        this.manageTeamError = 'Failed to delete team. Please try again.'
        this.disabled = false // re-enable the button if deletion fails
        return
      }
      this.clearFields()
      this.retrieveTeamData()
      this.hideModal(true)
      this.disabled = false // re-enable the button after deletion
    },
    // function to toggle the association of an agent with the selected team
    toggleAgentAssociation(agent, isChecked) {
      let associatedProfiles = this.teamAssociatedProfiles || []
      if (isChecked) {
        // add the agent to the associations if checked
        associatedProfiles.push({
          profile_id: agent.id,
          profile_type: agent.profile_type
        })
      } else {
        // remove the agent from the associations if unchecked
        associatedProfiles = associatedProfiles.filter(a => a.profile_id !== agent.id)
      }
      // update the selected team with the new associations
      this.selectedTeam.associations = associatedProfiles
    },
  },
  computed: {
    ...mapGetters("resources", ["teamData", "teamDataLoadingStatus", "agentData", "agentDataLoadingStatus"]),
    // computed property to filter teams based on the search input
    filteredTeams() {
      const searchLower = (this.search || '').toLowerCase()
      // filter teams that include the search term in their name
      return this.teamData
        .filter(team =>
          team.team_name.toLowerCase().includes(searchLower)
        )
        .sort((a, b) => a.team_name.localeCompare(b.team_name))
    },
    // computed property of the sorted agent data
    sortedAgentData() {
      // sort agents by name
      return this.agentData.sort((a, b) => a.name.localeCompare(b.name))
    },
    teamAssociatedProfiles() {
      // return the associated profiles of the selected team
      return (this.selectedTeam.associations || []).map(a => ({
        profile_id: a.profile_id,
        profile_type: a.profile_type
      }))
    },
    // computed property to get the current page title
    getPageTitle() {
      // return the title based on the current page
      if (this.page === TEAM_EDIT_PG) return 'Manage Teams'
      if (this.page === TEAM_DELETE_PG) return 'Delete Team'
      if (this.page === TEAM_ASSIGN_PG) return ''
      return ''
    },
    // properties to access the page constants
    TEAM_EDIT_PG() {
      return TEAM_EDIT_PG
    },
    TEAM_DELETE_PG() {
      return TEAM_DELETE_PG
    },
    TEAM_ASSIGN_PG() {
      return TEAM_ASSIGN_PG
    }
  },
  mounted: function() {
    // load teams when the component is mounted
    this.hideErrors()
    this.clearFields()
  },
};
</script>

<style>
.edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}
.team-list-item:hover .edit-icon {
  opacity: 1;
}
.selected-team {
  background-color: #E6F0FB !important; /* light cyan background for selected team */
}
.profile-checkbox label {
  opacity: 1; /* ensure checkbox label is fully opaque */
}
</style>
